/**
 * Sandbox service — mints a test connect-session on behalf of the logged-in merchant.
 * Replicates the auth pattern from connect_service.ts: Bearer access token from the
 * 'client-metadata' localStorage entry.  The sandbox endpoint is portal-JWT-gated
 * (same gate as every other /account/* endpoint).
 *
 * Rate-cap: the backend enforces 200 sandbox sessions per hour per merchant.
 * On breach it returns SANDBOX_004 in the error body — surface it to the developer.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

export interface SandboxSessionRequest {
    // Order total as a DECIMAL STRING, e.g. "49.99" — the backend parses it as a
    // string (POST /account/sandbox/connect-session; matches POST /v1/payments).
    // Sending a number produces `{"amount":49.99}`, which the Go handler rejects
    // with 400 SANDBOX_002 (cannot unmarshal number into string). Omit for an
    // identity-only session (scope = identity, no charge, no money).
    amount?: string;
    currency?: string;
    description?: string;
    line_items?: {
        name: string;
        description?: string;
        quantity: number;
        // Unit price in CENTS (backend field is `unit_price_cents`).
        unit_price_cents: number;
    }[];
    // Breakdown fields — optional, display-only (the popup renders them as
    // separate rows). The backend does NOT sum these against `amount`, so the
    // caller must ensure amount === subtotal_cents + shipping_cents (+ fee_cents).
    subtotal_cents?: number;
    shipping_cents?: number;
    fee_cents?: number;
}

export interface SandboxSessionResponse {
    connect_token: string;
    // order_id is null for identity-only sessions (no order is created).
    order_id: string | null;
    expires_at: string;
    popup_path: string;
}

export class SandboxApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'SandboxApiError';
    }
}

export interface SandboxExecRequest {
    endpoint: string;
    params: Record<string, unknown>;
}

export interface SandboxExecResponse {
    status: number;
    method: string;
    path: string;
    response: Record<string, unknown>;
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = await response.json() as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new SandboxApiError(message ?? `HTTP ${response.status}`, response.status, code);
}

export const apiSandboxService = {
    async mintSession(body: SandboxSessionRequest): Promise<SandboxSessionResponse> {
        const response = await fetch(`${BACKEND_URL}/account/sandbox/connect-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<SandboxSessionResponse>;
    },

    // Dev-only + prod-pinned: mints a LIVE connect-session (cs_live_ token +
    // live order) for the dev-portal demo store, so its checkout moves real
    // wallet balances (customer debited, merchant credited) and fires real
    // notifications. Identical request/response shape to mintSession. The
    // backend 404s outside a development environment — callers should treat
    // a 404 as "not available here" and fall back to mintSession() rather
    // than surface it as a checkout error.
    async mintDevDemoSession(body: SandboxSessionRequest): Promise<SandboxSessionResponse> {
        const response = await fetch(`${BACKEND_URL}/account/dev-demo/connect-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<SandboxSessionResponse>;
    },

    async exec(body: SandboxExecRequest): Promise<SandboxExecResponse> {
        const response = await fetch(`${BACKEND_URL}/account/sandbox/exec`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<SandboxExecResponse>;
    }
};
